import {
	type FrontendPlacementMessage,
	type AnyFrontendStage,
} from '@post-office/message-maker-browser/types';

import { useEffect, useRef } from 'react';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import { PlacementFrontend } from '@post-office/browser--jsm-saf/jsm-live-chat-notifications';

import { type MessageTemplateContext } from '../../../index';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

/**
 * JSM icon used as the OS-level notification thumbnail.
 *
 * Sourced from the Jira `live-chat-common` ProjectSubscription hook (see
 * `jira/src/packages/servicedesk/live-chat-common/src/hooks/ProjectSubscription.tsx`).
 *
 * Encoded as a URL-encoded SVG data URL rather than base64 because Safari
 * does not render base64 SVGs in the Web Notifications API. Keep this in sync
 * with the Jira-side copy if the icon ever changes.
 */
const JSM_ICON_DATA_URL =
	'data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%3E%3Cpath%20fill%3D%22%23ffc716%22%20d%3D%22M0%206a6%206%200%200%201%206-6h12a6%206%200%200%201%206%206v12a6%206%200%200%201-6%206H6a6%206%200%200%201-6-6z%22%2F%3E%3Cpath%20fill%3D%22%23101214%22%20d%3D%22M13.744%2010.156h4.578c.688%200%20.922.654.503%201.174l-7.16%208.837c-2.314-1.845-2.096-4.763-.402-6.892zm-3.538%203.521H5.63c-.688%200-.923-.654-.504-1.173l7.16-8.837c2.315%201.844%202.063%204.728.386%206.875z%22%2F%3E%3C%2Fsvg%3E';

/**
 * Build the deep-link URL used when the user clicks the browser notification.
 *
 * Example: `https://abinashnayakjsm.jira-dev.com/jira/servicedesk/projects/JHC/live-chat`
 */
const buildLiveChatUrl = (
	tenantUrl?: string,
	projectKey?: string,
	conversationId?: string,
): string | undefined => {
	if (!tenantUrl || !projectKey) {
		return undefined;
	}
	const trimmed = tenantUrl.replace(/\/$/, '');
	const base = `${trimmed}/jira/servicedesk/projects/${projectKey}/live-chat`;
	return conversationId ? `${base}?conversationId=${encodeURIComponent(conversationId)}` : base;
};

type NotificationParams = {
	title: string;
	description: string;
	projectKey?: string;
	tenantUrl?: string;
	conversationId?: string;
};

type FireAnalyticsEvent = ReturnType<typeof useFirePostOfficeAnalyticsEvents>['fireAnalyticsEvent'];

/**
 * Build a stable, cross-tab deduplication key for a notification.
 *
 * Uses the null byte (\x00) as a field separator to prevent key collisions
 * from titles/descriptions that naturally contain hyphens or other delimiters.
 */
const buildNotificationLockKey = (
	title: string,
	description: string,
	conversationId?: string,
): string => {
	const identity = [title, description, conversationId ?? ''].join('\x00');
	return `jsm-live-chat-notification:${identity}`;
};

/**
 * How long (ms) the winning tab holds the deduplication lock after dispatching.
 *
 * All other tabs race to acquire the same lock within this window. Because the
 * subscription event reaches all tabs nearly simultaneously (~0–50 ms apart),
 * 3 000 ms is a safe margin: long enough to cover any realistic tab jitter,
 * short enough that a genuinely new message with the same text fired seconds
 * later is not accidentally suppressed.
 */
const NOTIFICATION_LOCK_HOLD_MS = 3_000;

/**
 * Create and configure a Notification object with its onclick handler.
 */
const dispatchNotification = (
	{ title, description, projectKey, tenantUrl, conversationId }: NotificationParams,
	fireAnalyticsEvent: FireAnalyticsEvent,
): void => {
	try {
		const notification = new Notification(title, {
			body: description,
			icon: JSM_ICON_DATA_URL,
		});

		notification.onclick = (event: Event) => {
			// eslint-disable-next-line @atlassian/ssr-linting/no-dom-globals-all-member-expressions
			event.preventDefault();
			const target = buildLiveChatUrl(tenantUrl, projectKey, conversationId);
			if (!target) {
				notification.close();
				return;
			}
			fireAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'notification',
				actionSubjectId: 'jsmLiveChatNotifications',
				eventType: 'ui',
				attributes: {
					targetUrl: target,
					tenantUrl,
					projectKey,
					conversationId,
					title,
				},
			});
			const opened = globalThis?.window?.open(target, '_blank', 'noopener,noreferrer');
			if (opened) {
				opened.focus();
			}
			notification.close();
		};
	} catch {
		// Some browsers throw on certain notification options. Swallow — the
		// notification is best-effort and must never break the placement.
	}
};

/**
 * Dispatch a browser notification using the Web Notifications API.
 * Cross-tab deduplication via Web Locks API is used when available.
 * Fallback: if the Web Locks API is unavailable (non-secure context, very old
 * browsers, or SSR) we dispatch without deduplication — same as before.
 *
 * On click, focus the existing tab / open a new tab pointing at the live chat
 * page for the given project + (optional) conversation.
 */
const triggerBrowserNotification = (
	{ title, description, projectKey, tenantUrl, conversationId }: NotificationParams,
	fireAnalyticsEvent: FireAnalyticsEvent,
): void => {
	if (
		typeof globalThis.window === 'undefined' ||
		typeof globalThis.window?.Notification === 'undefined'
	) {
		fireAnalyticsEvent({
			action: 'failed',
			actionSubject: 'notification',
			actionSubjectId: 'jsmLiveChatNotifications',
			eventType: 'track',
			attributes: {
				reason: 'window or notification not available',
				tenantUrl,
				projectKey,
				conversationId,
				title,
			},
		});
		return;
	}

	if (globalThis.window?.Notification?.permission !== 'granted') {
		fireAnalyticsEvent({
			action: 'failed',
			actionSubject: 'notification',
			actionSubjectId: 'jsmLiveChatNotifications',
			eventType: 'track',
			attributes: {
				reason: 'notification permission not granted',
				tenantUrl,
				projectKey,
				conversationId,
				title,
			},
		});
		return;
	}

	const notificationParams: NotificationParams = {
		title,
		description,
		projectKey,
		tenantUrl,
		conversationId,
	};

	// Use the Web Locks API for cross-tab deduplication when available.
	if (typeof globalThis.navigator?.locks?.request === 'function') {
		const lockKey = buildNotificationLockKey(title, description, conversationId);
		// `ifAvailable: true` makes the request non-blocking: the callback
		// receives the lock immediately if it is free, or `null` if another tab
		// already holds it for this exact notification identity.
		//
		// IMPORTANT: the callback must return a Promise that stays pending for
		// NOTIFICATION_LOCK_HOLD_MS. If we return synchronously (or resolve
		// immediately), the lock is released before other tabs have had a chance
		// to attempt acquisition — every tab would then win its own lock and fire
		// its own notification, defeating the deduplication entirely.
		void globalThis.navigator.locks.request(lockKey, { ifAvailable: true }, (lock) => {
			if (lock === null) {
				// Another tab already holds the lock for this notification — skip.
				return;
			}
			fireAnalyticsEvent({
				action: 'success',
				actionSubject: 'notification',
				actionSubjectId: 'jsmLiveChatNotifications',
				eventType: 'track',
				attributes: {
					reason: 'lock acquired',
					tenantUrl,
					projectKey,
					conversationId,
					title,
				},
			});
			dispatchNotification(notificationParams, fireAnalyticsEvent);
			// Hold the lock for NOTIFICATION_LOCK_HOLD_MS so that other tabs
			// that arrive within that window see the lock as taken and bail out.
			return new Promise<void>((resolve) => {
				setTimeout(resolve, NOTIFICATION_LOCK_HOLD_MS);
			});
		});
		return;
	}

	// Fallback: Web Locks API is unavailable — dispatch without deduplication.
	dispatchNotification(notificationParams, fireAnalyticsEvent);
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation(),
		Component: ({
			title,
			description,
			projectKey,
			tenantUrl,
			conversationId,
		}: MessageTemplateContext) => {
			// Each message instance is mounted with a unique React key
			// (`messageInstanceId`) so this effect runs exactly once per incoming
			// GraphQL subscription event. `firedRef` is a defensive guard against
			// React 18 strict-mode double-invocation in development.
			const firedRef = useRef(false);
			const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

			useEffect(() => {
				if (firedRef.current) {
					return;
				}
				firedRef.current = true;

				triggerBrowserNotification(
					{
						title,
						description,
						projectKey,
						tenantUrl,
						conversationId,
					},
					fireAnalyticsEvent,
				);
			}, [title, description, projectKey, tenantUrl, conversationId, fireAnalyticsEvent]);

			// This message template is intentionally headless — the visible
			// surface is the OS-level browser notification.
			return null;
		},
	},
});
